import ESigningFormValidation from './ESigningFormValidation';
import ESigningReview from './ESigningReview';
import ESigningFormPersonalNumber from './ESigningForm.personalNumber';
import ESigningFormReceivers from './ESigningForm.receivers';
import ESigningFormFiles from './ESigningForm.files';
import ESigningFormApi from './ESigningForm.api';
import ESigningFormDatepicker from './ESigningForm.datepicker';
import ESigningFormTemplates from './ESigningForm.templates';

/**
 * Base class for E-Signing Agreement forms.
 * Contains shared logic for step 2+3, navigation, loading, payload and submit.
 * Extend this class in per-variant files to add step-1-specific logic.
 */
export default class ESigningFormBase {
    constructor() {
        if (document.getElementsByClassName('esigning-agreement').length < 1) {
            return;
        }

        this.review = new ESigningReview();
        this.validation = new ESigningFormValidation();
        this.isSubmitting = false;
        this.submitEndpoint = '/api/e-signing/v2/initiate-signing';
        this.initialStep1ContentHtml = document.getElementById('esigning-form-content-container')?.innerHTML || '';
        this.defaultTemplateLabel = document.querySelector('#signingTemplates .showcase a')?.textContent || '';

        ESigningFormDatepicker.init();

        this.initStep2And3EventListeners();

        const pageContentId = document.getElementById('pageContentId')?.value || '';
        const templatesEndpoint = `/api/e-signing/v2/esigning-templates?pageId=${pageContentId}`;
        ESigningFormTemplates.fetchSigningTemplatesAsync(this, templatesEndpoint);
        const templateList = document.getElementById('selectTemplate');
        if (templateList) {
            templateList.addEventListener('click', (evt) => ESigningFormTemplates.selectSigningTemplate(this, evt));
        }
        ESigningFormReceivers.fetchReceiverDataAsync(this, '/api/e-signing/get-receivers-for-digital-signing');

        this.toggleStep1NextVisibility(false);
    }

    toggleStep1NextVisibility(show) {
        const container = document.getElementById('Step1NextContainer');
        if (!container) return;

        container.style.display = show ? '' : 'none';
    }

    /**
     * @description Initialize step 2 and step 3 event listeners (shared across all form variants)
     * @returns void
     */
    initStep2And3EventListeners() {
        // Submit data button
        const postBtn = document.querySelector('#postESigningAgreement');
        if (postBtn) {
            postBtn.addEventListener('click', this.submitESigningData);
        }

        // Step 2 Back button
        const step2BackBtn = document.querySelector('#Step2Back');
        if (step2BackBtn) {
            step2BackBtn.addEventListener('click', this.handleStep2Back);
        }

        // Step 2 Next button - always clickable, fields are only validated when clicked
        const step2NextBtn = document.querySelector('#Step2Next');
        if (step2NextBtn) {
            step2NextBtn.addEventListener('click', this.handleStep2Next);
        }

        const step2VisibleFieldIds = ['ReceiverEmail', 'ReceiverPhoneNumber', 'ReceiverPersonalNumber'];
        step2VisibleFieldIds.forEach((id) => {
            const field = document.getElementById(id);
            if (!field) return;
            field.addEventListener('input', () => this.validation.clearFieldError(id));
        });

        const receiverEmailField = document.getElementById('ReceiverEmail');
        if (receiverEmailField) {
            receiverEmailField.addEventListener('input', this.syncSignerFromReceiverEmail);
            receiverEmailField.addEventListener('blur', this.syncSignerFromReceiverEmail);
            receiverEmailField.addEventListener('change', this.syncSignerFromReceiverEmail);
        }

        const receiverPersonalNumberField = document.getElementById('ReceiverPersonalNumber');
        if (receiverPersonalNumberField) {
            receiverPersonalNumberField.addEventListener('blur', () => {
                ESigningFormPersonalNumber.formatReceiverPersonalNumber(this);
            });
            receiverPersonalNumberField.addEventListener('change', () => {
                ESigningFormPersonalNumber.formatReceiverPersonalNumber(this);
            });
        }

        ['CurrentSignerName', 'CurrentSignerEmail', 'CurrentSignerPhoneNumber', 'CurrentSignerPersonalNumber']
            .forEach((id) => {
                const field = document.getElementById(id);
                if (!field) return;
                field.addEventListener('input', () => this.validation.clearFieldError(id));
            });

        const currentSignerPersonalNumberField = document.getElementById('CurrentSignerPersonalNumber');
        if (currentSignerPersonalNumberField) {
            const formatCurrentSignerPersonalNumber = () => {
                currentSignerPersonalNumberField.value = ESigningFormPersonalNumber.normalizePersonalNumber(
                    currentSignerPersonalNumberField.value
                );
            };
            currentSignerPersonalNumberField.addEventListener('blur', formatCurrentSignerPersonalNumber);
            currentSignerPersonalNumberField.addEventListener('change', formatCurrentSignerPersonalNumber);
        }

        const currentSignerEmail = document.getElementById('CurrentSignerEmail');
        const currentSignerEmailSummary = document.getElementById('CurrentSignerEmailSummary');
        if (currentSignerEmail && currentSignerEmailSummary) {
            const updateCurrentSignerEmailSummary = () => {
                currentSignerEmailSummary.textContent = currentSignerEmail.value.trim();
            };
            currentSignerEmail.addEventListener('input', updateCurrentSignerEmailSummary);
            currentSignerEmail.addEventListener('change', updateCurrentSignerEmailSummary);
        }

        // Step 3 Back button
        const step3BackBtn = document.querySelector('#Step3Back');
        if (step3BackBtn) {
            step3BackBtn.addEventListener('click', this.handleStep3Back);
        }

        // Step 3 Send for signing button
        const step3SendBtn = document.querySelector('#Step3Next');
        if (step3SendBtn) {
            step3SendBtn.addEventListener('click', this.submitESigningData);
        }
    }

    /**
     * @description Submit generic e-signing data
     * @returns void
     */
    submitESigningData = async () => {
        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.setSubmitButtonsDisabled(true);

        try {
            this.showLoading();
            const payload = await this.buildPayload();
            const response = await ESigningFormApi.postJson(this.submitEndpoint, payload);

            if (!response || response.status !== 200) {
                this.showErrorDialog();
                return;
            }

            this.showStep(4);
        } finally {
            this.hideLoading();
            this.setSubmitButtonsDisabled(false);
            this.isSubmitting = false;
        }
    };

    handleStep2Back = () => {
        this.showStep(1);
    };

    handleStep2Next = () => {
        ESigningFormPersonalNumber.formatReceiverPersonalNumber(this);
        const hasValidVisibleInputs = this.validateStep2VisibleInputs(true);
        const hasValidCurrentSigner = this.expandCurrentSignerSectionIfInvalid();
        if (!hasValidVisibleInputs || !hasValidCurrentSigner) return;
        this.review.updateReviewData();
        this.showStep(3);
    };

    handleStep3Back = () => {
        this.showStep(2);
    };

    /**
     * @description Fall back to the receiver's email as signer id if none was set explicitly
     * @returns void
     */
    syncSignerFromReceiverEmail = () => {
        const signerInput = document.getElementById('Signer');
        const receiverEmailInput = document.getElementById('ReceiverEmail');
        if (!signerInput || !receiverEmailInput) return;

        const signer = signerInput.value?.trim() ?? '';
        const receiverEmail = receiverEmailInput.value?.trim() ?? '';
        if (signer.length === 0 && receiverEmail.length > 0) {
            signerInput.value = receiverEmail;
        }
    };

    validateStep2InputField(inputId, showRequired) {
        const inputElement = document.getElementById(inputId);
        if (!inputElement) return true;

        const value = inputElement.value?.trim() ?? '';

        if (inputId === 'ReceiverEmail') {
            if (!value) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.receiverEmailRequired);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            if (!this.validation.isValidEmail(value)) {
                if (!showRequired) { this.validation.clearFieldError(inputId); return false; }
                this.validation.showFieldError(inputId, eSigningTranslation.receiverEmail);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        if (inputId === 'ReceiverPhoneNumber') {
            if (!value) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.receiverPhoneNumberRequired);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            if (!this.validation.isValidMobileNumber(value)) {
                if (!showRequired) { this.validation.clearFieldError(inputId); return false; }
                this.validation.showFieldError(inputId, eSigningTranslation.mobilePattern);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        if (inputId === 'ReceiverPersonalNumber') {
            const normalizedPersonalNumber = ESigningFormPersonalNumber.getReceiverPersonalNumberValue(this);
            if (showRequired && !ESigningFormPersonalNumber.isReceiverPersonalNumberFromDropdown() && normalizedPersonalNumber !== value) {
                inputElement.value = normalizedPersonalNumber;
            }
            if (!normalizedPersonalNumber) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.personalNumberRegexPersonal);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            if (!this.validation.isValidPersonalNumber(normalizedPersonalNumber)) {
                if (!showRequired) { this.validation.clearFieldError(inputId); return false; }
                this.validation.showFieldError(inputId, eSigningTranslation.personalNumberRegexPersonal);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        return true;
    }

    validateStep2VisibleInputs(showRequired) {
        const fields = ['ReceiverEmail', 'ReceiverPhoneNumber', 'ReceiverPersonalNumber'];
        return fields
            .map((fieldId) => this.validateStep2InputField(fieldId, showRequired))
            .every(Boolean);
    }

    validateCurrentSignerField(inputId, showRequired) {
        const inputElement = document.getElementById(inputId);
        if (!inputElement) return true;

        const value = inputElement.value?.trim() ?? '';

        if (inputId === 'CurrentSignerName') {
            if (!value) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.currentSignerNameRequired);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        if (inputId === 'CurrentSignerEmail') {
            if (!value) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.receiverEmailRequired);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            if (!this.validation.isValidEmail(value)) {
                if (!showRequired) { this.validation.clearFieldError(inputId); return false; }
                this.validation.showFieldError(inputId, eSigningTranslation.receiverEmail);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        if (inputId === 'CurrentSignerPhoneNumber') {
            if (!value) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.receiverPhoneNumberRequired);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            if (!this.validation.isValidMobileNumber(value)) {
                if (!showRequired) { this.validation.clearFieldError(inputId); return false; }
                this.validation.showFieldError(inputId, eSigningTranslation.mobilePattern);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        if (inputId === 'CurrentSignerPersonalNumber') {
            const normalizedPersonalNumber = ESigningFormPersonalNumber.normalizePersonalNumber(value);
            if (!normalizedPersonalNumber) {
                if (showRequired) this.validation.showFieldError(inputId, eSigningTranslation.personalNumberRegexPersonal);
                else this.validation.clearFieldError(inputId);
                return false;
            }
            if (!this.validation.isValidPersonalNumber(normalizedPersonalNumber)) {
                if (!showRequired) { this.validation.clearFieldError(inputId); return false; }
                this.validation.showFieldError(inputId, eSigningTranslation.personalNumberRegexPersonal);
                return false;
            }
            this.validation.clearFieldError(inputId);
            return true;
        }

        return true;
    }

    validateCurrentSignerFields(showRequired) {
        if (document.getElementById('step2View')?.dataset.includeSellerSigner !== 'true') {
            return true;
        }

        const fields = ['CurrentSignerName', 'CurrentSignerEmail', 'CurrentSignerPhoneNumber', 'CurrentSignerPersonalNumber'];
        return fields
            .map((fieldId) => this.validateCurrentSignerField(fieldId, showRequired))
            .every(Boolean);
    }

    /**
     * @description Validate the Seller ("Säljare") fields and expand the section if it holds invalid/missing
     * data, so the user can see what needs to be filled in. Safe to call repeatedly (no-op once expanded).
     * @returns {boolean} true if the Seller fields are valid
     */
    expandCurrentSignerSectionIfInvalid = () => {
        const isValid = this.validateCurrentSignerFields(true);
        if (!isValid) {
            const toggleControl = document.getElementById('CurrentSignerToggle');
            if (toggleControl) farmday.modules.toggle.show(toggleControl);
        }
        return isValid;
    };

    showStep(stepNumber) {
        document.querySelectorAll('.steps').forEach((step) => step.classList.add('hidden'));
        const selectedStep = document.querySelector(`#step${stepNumber}View`);
        if (selectedStep) {
            selectedStep.classList.remove('hidden');
        }
    }

    buildPayload = async () => {
        const getVal = (id) => document.getElementById(id)?.value || '';
        const getArrayVal = (id) => {
            const rawValue = getVal(id)?.trim();
            if (!rawValue) {
                return [];
            }

            const parsedValue = JSON.parse(rawValue);
            return Array.isArray(parsedValue)
                ? parsedValue
                : [];
        };

        const pdfBytes = await ESigningFormFiles.getPdfFileBytes();
        const documentFileInput = document.getElementById('documentFile');
        const pdfName = documentFileInput?.files?.[0]?.name || '';
        const premiumProductItemInput = document.getElementById('premiumProductItem');
        const premiumProductItemPriceRaw = premiumProductItemInput?.dataset?.price || '';
        const premiumProductItemPrice = premiumProductItemPriceRaw === ''
            ? null
            : Number.parseFloat(premiumProductItemPriceRaw);
        const premiumProductName = premiumProductItemInput?.dataset?.name || '';
        const contractQuantity = getVal('contractQuantity') || getVal('premiumHectare');
        const currentSigner = {
            name: getVal('CurrentSignerName'),
            email: getVal('CurrentSignerEmail'),
            mobileNumber: getVal('CurrentSignerPhoneNumber'),
            personalNumber: ESigningFormPersonalNumber.normalizePersonalNumber(getVal('CurrentSignerPersonalNumber'))
        };
        const selectedSigner = {
            name: getVal('ReceiverName'),
            email: getVal('ReceiverEmail'),
            mobileNumber: getVal('ReceiverPhoneNumber'),
            personalNumber: ESigningFormPersonalNumber.getReceiverPersonalNumberValue(this)
        };
        const includeSellerSigner = document.getElementById('step2View')?.dataset.includeSellerSigner === 'true';

        return {
            agreementName: getVal('AgreementNumber'),
            templateId: getVal('signingTemplate'),
            customerNumber: getVal('customerNumberElementId'),
            pdfFile: pdfBytes,
            pdfName: pdfName,
            duration: parseInt(getVal('signingDuration')) || 30,
            deadlineTime: getVal('deadlineTime') || null,
            pageId: parseInt(getVal('pageContentId')) || null,
            templateSettingsBlockId: parseInt(getVal('templateSettingsBlockId')) || null,
            signers: includeSellerSigner ? [currentSigner, selectedSigner] : [selectedSigner],
            // Premium agreement fields (empty for non-Premium variants)
            premiumProductItem: getVal('premiumProductItem'),
            premiumProductName: premiumProductName,
            price: Number.isNaN(premiumProductItemPrice) ? null : premiumProductItemPrice,
            premiumDeliveryPeriod: getVal('premiumDeliveryPeriod'),
            premiumHectare: contractQuantity,
            contractQuantity: contractQuantity,
            climateNatureMeasures: getArrayVal('climateNatureMeasures'),
        };
    };

    showLoading = () => {
        const loader = document.querySelector('#agreement-loader');
        const btn = document.querySelector('.btnSaveAgreement');
        if (loader) loader.style.display = 'block';
        if (btn) btn.classList.add('disabled');
    };

    hideLoading = () => {
        const loader = document.querySelector('#agreement-loader');
        const btn = document.querySelector('.btnSaveAgreement');
        if (loader) loader.style.display = 'none';
        if (btn) btn.classList.remove('disabled');
    };

    showErrorDialog = () => {
        const dialog = document.querySelector('#informationDialog');
        if (!dialog) return;
        const span = dialog.querySelector('span.popup-type');
        if (span) span.classList.add('warning');
        dialog.classList.remove('hidden');
    };

    setSubmitButtonsDisabled = (isDisabled) => {
        const submitButtonIds = ['Step3Next', 'postESigningAgreement'];

        submitButtonIds.forEach((id) => {
            const button = document.getElementById(id);
            if (button) {
                button.disabled = isDisabled;
            }
        });
    };
}
